import { fetchWrapper } from "../helpers/fetch-wrapper";
import type {
  Notification,
  NotificationQueryParams,
  UnreadCountResponse,
  NotificationType,
  NotificationSeverity,
} from "@/types";

const baseUrl = `${import.meta.env.VITE_API_URL}/notifications`;

export const notificationsApi = {
  /**
   * Get user notifications with optional filtering and pagination
   */
  async getNotifications(
    params: NotificationQueryParams = {}
  ): Promise<Notification[]> {
    const queryParams = new URLSearchParams();

    if (params.isRead !== undefined) {
      queryParams.append("isRead", params.isRead.toString());
    }
    if (params.category !== undefined) {
      queryParams.append("category", params.category.toString());
    }
    if (params.severity !== undefined) {
      queryParams.append("severity", params.severity.toString());
    }
    if (params.page) {
      queryParams.append("page", params.page.toString());
    }
    if (params.pageSize) {
      queryParams.append("pageSize", params.pageSize.toString());
    }

    const url = queryParams.toString()
      ? `${baseUrl}?${queryParams.toString()}`
      : baseUrl;

    return await fetchWrapper.get(url);
  },

  /**
   * Get count of unread notifications
   */
  async getUnreadCount(): Promise<number> {
    const response: UnreadCountResponse = await fetchWrapper.get(
      `${baseUrl}/unread/count`
    );
    return response.count;
  },

  /**
   * Get a specific notification by ID
   */
  async getNotificationById(id: string): Promise<Notification> {
    return await fetchWrapper.get(`${baseUrl}/${id}`);
  },

  /**
   * Mark a notification as read
   */
  async markAsRead(id: string): Promise<void> {
    await fetchWrapper.put(`${baseUrl}/${id}/read`, {});
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    await fetchWrapper.put(`${baseUrl}/read-all`, {});
  },

  /**
   * Delete a notification
   */
  async deleteNotification(id: string): Promise<void> {
    await fetchWrapper.delete(`${baseUrl}/${id}`);
  },

  /**
   * Broadcast a notification (SuperAdmin only)
   */
  async broadcast(request: {
    type: NotificationType;
    title: string;
    message: string;
    severity?: NotificationSeverity;
    actionUrl?: string;
    actionText?: string;
    targetOrgId?: string;
    targetUserId?: string;
  }): Promise<{ notificationCount: number; notificationIds: string[] }> {
    return await fetchWrapper.post(`${baseUrl}/broadcast`, request);
  },
};
